import { useState, useContext, useEffect, useRef } from 'react';
import { GlobalStateContext } from '@utils/GlobalContext';
import { useChat } from '@utils/ChatContext';
import Markdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import './chatbot.scss';

function ChatBot() {
  const { chatEnabled } = useContext(GlobalStateContext);
  const { messages, chatVisible, setChatVisible, toggleChat, sendMessage, loading, streamingContent, isStreaming, currentSection } = useChat();
  const [input, setInput] = useState('');
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages, loading, streamingContent]);

  useEffect(() => {
    if (chatVisible) {
      inputRef.current?.focus();
    }
  }, [chatVisible]);

  const handleSubmit = (e) => {
    e?.prependDefault?.();
    if (e?.preventDefault) e.preventDefault();
    const text = input.trim();
    if (!text || loading || isStreaming) return;
    setInput('');
    sendMessage(text, currentSection);
  };

  if (!chatEnabled) return null;

  return (
    <>
      <button
        className="chatbot-fab"
        onClick={toggleChat}
        aria-label="Apri chat assistente"
        title="Assistente registrazione"
      >
        <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
          <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
        </svg>
      </button>

      {chatVisible && (
        <div className="chatbot-window">
          <div className="chatbot-header">
            <h3>Assistente JataPay</h3>
            <button onClick={toggleChat} aria-label="Chiudi chat">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="18" y1="6" x2="6" y2="18" />
                <line x1="6" y1="6" x2="18" y2="18" />
              </svg>
            </button>
          </div>

          <div className="chatbot-messages">
            {messages.length === 0 && (
            <div className="chatbot-placeholder">
              <p>👋 Ciao! Sono l'assistente di supporto JataPay.</p>
              <p>Fammi pure qualsiasi domanda!</p>
            </div>
            )}

            {messages.map((msg) => (
              <div key={msg.id} className={`chatbot-message ${msg.role}`}>
                {msg.section && <span className="section-tag">{msg.section}</span>}
                <Markdown remarkPlugins={[remarkGfm]}>{msg.content}</Markdown>
              </div>
            ))}

            {isStreaming && streamingContent && (
              <div className="chatbot-message assistant">
                <Markdown remarkPlugins={[remarkGfm]}>{streamingContent}</Markdown><span className="chatbot-streaming-cursor" />
              </div>
            )}

            {loading && !isStreaming && (
              <div className="chatbot-typing">
                <span />
                <span />
                <span />
              </div>
            )}

            <div ref={messagesEndRef} />
          </div>

          <div className="chatbot-disclaimer">
            L'assistente è basato su IA, può commettere errori. Verifica sempre le informazioni importanti.
          </div>

          <form className="chatbot-input-area" onSubmit={handleSubmit}>
            <input
              ref={inputRef}
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              placeholder="Scrivi un messaggio..."
              disabled={loading || isStreaming}
            />
            <button type="submit" disabled={!input.trim() || loading || isStreaming} aria-label="Invia">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                <line x1="22" y1="2" x2="11" y2="13" />
                <polygon points="22 2 15 22 11 13 2 9 22 2" />
              </svg>
            </button>
          </form>
        </div>
      )}
    </>
  );
}

export default ChatBot;
